import { computed, effect, inject, Injectable, Injector, signal, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { defaultStepsOrder } from '@config/app-steps-order.config';
import { appStepsConfig } from '@config/app-steps.config';
import {
    AllSubSteps,
    AppStep,
    ApplicationInformationSubSteps,
    MainSteps,
    PaymentSubSteps,
    PersonalDetailsSubSteps,
    StepsOrderConfig,
    SummarySubSteps,
} from '@models/config/app-structure.model';

/** Toggle to restore the SUMMARY step. Currently hidden because the design replaces it with the Payment INTRO "Almost There" page. */
const SHOW_SUMMARY_STEP = false;
import { ApplicationProductsService } from '@services/api-calls/application-products.service';
import { PaymentDataService } from '@services/api-calls/payment-data.service';
import { PaymentVerificationService } from '@services/api-calls/payment-verification.service';
import { ApplicationStateService } from '@services/application-state.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import { GlobalStateService } from '@services/global-state.service';
import { SubstepValidationToggleService } from '@services/testing/substep-validation-toggle.service';
import { UnsavedChangesService } from '@services/unsaved-changes.service';
import { ExistingInsuranceSubstepFormEnum, PaymentTypeEnum, ResidentialInformationFormEnum, YesNoOptionsEnum } from 'app/enums/form/form.enum';
import { filter, map, Observable, of, startWith, Subscription } from 'rxjs';

interface ConditionalSubstepConfig {
    step: MainSteps;
    substep: string;
    condition: () => Observable<boolean>;
}

@Injectable({
    providedIn: 'root',
})
export class StepperService {
    private baseSteps = signal(appStepsConfig.steps);
    private stepsOrder = signal<StepsOrderConfig>(defaultStepsOrder);

    /**
     * Complex AND/OR logic - SQL-like: (c0 AND c1) OR (c2 AND c3)
     *
     * Combines multiple form controls with custom boolean logic.
     * Use combineLatest to watch multiple observables and map to boolean expression.
     *
     * @example
     * {
     *     step: MainSteps.APPLICATION_INFORMATION,
     *     substep: ApplicationInformationSubSteps.TRUSTEE,
     *     condition: () => combineLatest([
     *         this.controlValue$([MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.SMOKER])
     *             .pipe(map(v => v === YesNoOptionsEnum.YES)),
     *         this.controlValue$([MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION, ResidentialInformationFormEnum.STATE_CODE])
     *             .pipe(map(v => v === 'CA')),
     *         this.controlValue$([MainSteps.PRODUCT_SELECTION, ProductSelectionSubSteps.PRODUCT_SELECTION, 'COVERAGE'])
     *             .pipe(map(v => v > 100000)),
     *         this.controlValue$([MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, 'AGE'])
     *             .pipe(map(v => v < 30))
     *     ]).pipe(
     *         map(([smoker, isCA, highCoverage, youngAge]) => (smoker && isCA) || (highCoverage && youngAge))
     *     )
     * }
     */

    /**
     * Mix form controls + service observable state
     *
     * Shows how to combine form control values with external service observables.
     * Useful when substep visibility depends on both form state and application state.
     *
     * @example
     * {
     *     step: MainSteps.PAYMENT,
     *     substep: PaymentSubSteps.PAYMENT,
     *     condition: () => combineLatest([
     *         this.controlValue$([MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.FINANCE_INFORMATION, 'OCCUPATION'])
     *             .pipe(map(v => v === OccupationOptionsEnum.Employed)),
     *         this.someService.userRole$.pipe(map(role => role === 'admin'))
     *     ]).pipe(
     *         map(([isEmployed, isAdmin]) => isEmployed && isAdmin)
     *     )
     * }
     */

    private conditionalSubsteps: ConditionalSubstepConfig[] = [
        // MAILING_ADDRESS used to be conditional on a Yes/No question on the residential
        // info page. The question was removed — mailing address (purchased property) is
        // now always collected, so the substep is unconditionally visible.
        {
            step: MainSteps.APPLICATION_INFORMATION,
            substep: ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT,
            condition: () =>
                this.controlValue$([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.EXISTING_INSURANCE,
                    ExistingInsuranceSubstepFormEnum.HAS_EXISTING_INSURANCES,
                ]).pipe(map((value) => value === YesNoOptionsEnum.YES)),
        },
        {
            step: MainSteps.PAYMENT,
            substep: PaymentSubSteps.SECONDARY_PAYMENT_METHOD,
            condition: () => {
                const verifiedSignal = computed(() =>
                    this.paymentVerificationService.getVerificationState(PaymentTypeEnum.PRIMARY)().verified
                );
                return toObservable(verifiedSignal);
            },
        },
    ];

    private substepVisibilitySignals = new Map<string, Signal<boolean>>();
    private substepVisibilityStates = new Map<string, boolean>();
    private badgeRemovalSubscriptions = new Map<string, Subscription>();
    newlyAddedSubsteps = signal<Map<string, number>>(new Map());

    currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects || event.url),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url },
    );

    private route = inject(ActivatedRoute);

    private extractToken(url: string): string | null {
        const segments = url.split('/').filter((s) => s);
        const lastSegment = segments[segments.length - 1];

        if (segments.length > 0) {
            return lastSegment;
        }
        return null;
    }

    private getUrlWithoutToken(url: string): string {
        const token = this.extractToken(url);
        if (!token) return url;
        return url.replace(`/${token}`, '');
    }

    getToken(): string | null {
        // Traverse route tree to find guid param
        let currentRoute: ActivatedRoute | null = this.route.root;
        while (currentRoute) {
            if (currentRoute.snapshot.params['guid']) {
                return currentRoute.snapshot.params['guid'];
            }
            currentRoute = currentRoute.firstChild;
        }
        return null;
    }

    private applicationStateService = inject(ApplicationStateService);
    private applicationProductsService = inject(ApplicationProductsService);
    private paymentDataService = inject(PaymentDataService);
    private injector = inject(Injector);

    private _unsavedChangesService: UnsavedChangesService | null = null;
    private getUnsavedChangesService(): UnsavedChangesService {
        if (!this._unsavedChangesService) {
            this._unsavedChangesService = this.injector.get(UnsavedChangesService);
        }
        return this._unsavedChangesService;
    }

    constructor(
        private router: Router,
        private applicationFormService: ApplicationFormService,
        private formRegistry: FormRegistryService,
        private substepValidationToggle: SubstepValidationToggleService,
        private globalStateService: GlobalStateService,
        private paymentVerificationService: PaymentVerificationService,
    ) {
        // Initialize visibility signals for all conditional substeps
        this.conditionalSubsteps.forEach((config) => {
            const condition$ = config.condition();
            const visibilitySignal = toSignal(condition$, {
                initialValue: false,
            });

            const key = `${config.step}:${config.substep}`;
            this.substepVisibilitySignals.set(key, visibilitySignal);
        });

        // Statements & Agreements + ACH Disclaimer are hidden as standalone substeps —
        // both forms are surfaced on the FINAL_CONSENTS page in Payment. Their groups
        // stay registered so their validity feeds the FINAL_CONSENTS completion check.
        this.substepVisibilitySignals.set(
            `${MainSteps.APPLICATION_INFORMATION}:${ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS}`,
            signal(false),
        );
        this.substepVisibilitySignals.set(
            `${MainSteps.PAYMENT}:${PaymentSubSteps.ACH_DISCLAIMER}`,
            signal(false),
        );

        // Submit substep hidden — final submission now fires from the FINAL_CONSENTS
        // page's Next button. The submit page itself stays available as a redirect
        // target only when status flips to "completed".
        this.substepVisibilitySignals.set(
            `${MainSteps.SUBMISSION}:${PaymentSubSteps.SUBMIT}`,
            signal(false),
        );

        // SUMMARY step temporarily hidden — replaced by Payment INTRO "Almost There" page.
        // Flip SHOW_SUMMARY_STEP to true (or remove these two lines) to restore.
        if (!SHOW_SUMMARY_STEP) {
            this.substepVisibilitySignals.set(
                `${MainSteps.SUMMARY}:${SummarySubSteps.SUMMARY}`,
                signal(false),
            );
        }

        this.registerInitialSubsteps();
        this.setupFormVisibilityEffect();

        // Redirect to Summary when application is declined
        effect(() => {
            if (this.applicationProductsService.isDeclined()) {
                const url = this.currentUrl();
                if (url && !url.includes('/summary')) {
                    const token = this.getToken();
                    if (token) {
                        this.router.navigate(['/summary', token]);
                    }
                }
            }
        });

        // Redirect to Product Selection when emc_risk is missing (skip if declined)
        effect(() => {
            if (this.applicationProductsService.isDeclined()) return;
            if (this.applicationProductsService.isMissingEmcRisk()) {
                const url = this.currentUrl();
                if (url && !url.includes('/personal-details') && !url.includes('/product-selection')) {
                    const token = this.getToken();
                    if (token) {
                        this.router.navigate(['/product-selection', token]);
                    }
                }
            }
        });
    }

    private registerInitialSubsteps(): void {
        const orderedSteps = this.orderedSteps();

        orderedSteps.forEach((step) => {
            step.subSteps.forEach((subStep) => {
                const visibilitySignal = this.getSubstepVisibility(step.step, subStep.step);
                const formGroup = this.applicationFormService.getMainStepFormGroup(step.step)?.get(subStep.step as string) as FormGroup;

                if (formGroup) {
                    this.formRegistry.registerSubstep(step.step, subStep.step, formGroup, visibilitySignal);
                }
            });
        });
    }

    getSubstepVisibility(mainStep: MainSteps, subStep: AllSubSteps): Signal<boolean> {
        const key = `${mainStep}:${subStep}`;
        return this.substepVisibilitySignals.get(key) ?? signal(true);
    }

    private setupFormVisibilityEffect(): void {
        effect(() => {
            const orderedSteps = this.orderedSteps();

            orderedSteps.forEach((step) => {
                step.subSteps.forEach((subStep) => {
                    const visibilitySignal = this.getSubstepVisibility(step.step, subStep.step);
                    const isVisible = visibilitySignal();
                    const mainStepFormGroup = this.applicationFormService.getMainStepFormGroup(step.step);
                    const existsInForm = mainStepFormGroup?.contains(subStep.step as string) ?? false;

                    const key = `${step.step}:${subStep.step}`;
                    const previousVisible = this.substepVisibilityStates.get(key);

                    // Update state tracking
                    this.substepVisibilityStates.set(key, isVisible);

                    if (isVisible && !existsInForm) {
                        this.applicationFormService.addSubstepFormGroup(step.step, subStep.step as string);

                        // Disable newly added substep only on a hard lock (completed/declined).
                        // Payment start no longer freezes upstream steps — those stay editable.
                        const shouldDisable =
                            this.applicationStateService.isCompleted() ||
                            this.applicationProductsService.isDeclined();
                        if (shouldDisable) {
                            const newFormGroup = mainStepFormGroup?.get(subStep.step as string);
                            if (newFormGroup && !newFormGroup.disabled) {
                                newFormGroup.disable();
                            }
                        }

                        // Only trigger on FALSE → TRUE transition (previousVisible === false)
                        // On first run, previousVisible is undefined, so skip
                        // On completed apps, skip — visibility changes are from data restoration, not user actions
                        if (previousVisible === false && !this.applicationStateService.isCompleted()) {
                            this.handleSubstepAdded(step.step, subStep.step);
                        }
                    } else if (!isVisible && existsInForm) {
                        this.applicationFormService.removeSubstepFormGroup(step.step, subStep.step as string);
                        if (previousVisible === true) {
                            this.handleSubstepRemoved(step.step, subStep.step);
                        }
                    }
                });
            });
        });
    }

    private getSubstepCompletion(mainStep: MainSteps, subStep: AllSubSteps): boolean {
        // Submit step: only complete when application status is "completed"
        if (mainStep === MainSteps.SUBMISSION && subStep === PaymentSubSteps.SUBMIT) {
            return this.applicationStateService.isCompleted();
        }

        // Force complete on completed apps (except Submit which is handled above)
        if (this.applicationStateService.isCompleted()) return true;

        // Force complete on declined apps
        if (this.applicationProductsService.isDeclined()) return true;

        // Force complete Payment Information when primary payment data exists (locked)
        if (mainStep === MainSteps.PAYMENT && subStep === PaymentSubSteps.PAYMENT_INFORMATION) {
            if (this.paymentDataService.hasPrimaryPaymentData()) return true;
        }

        // Payment method steps: use verification service signals (reactive!)
        if (
            mainStep === MainSteps.PAYMENT &&
            (subStep === PaymentSubSteps.PRIMARY_PAYMENT_METHOD || subStep === PaymentSubSteps.SECONDARY_PAYMENT_METHOD)
        ) {
            const type = subStep === PaymentSubSteps.PRIMARY_PAYMENT_METHOD ? PaymentTypeEnum.PRIMARY : PaymentTypeEnum.SECONDARY;
            return this.paymentVerificationService.getVerificationState(type)().verified;
        }

        // Identity Verification Consent — identity + medical only.
        // Statements, fraud and ACH disclosures moved to the FINAL_CONSENTS substep
        // in the Payment step (the new gate for Submit).
        if (mainStep === MainSteps.PERSONAL_DETAILS && subStep === PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT) {
            return this.formRegistry
                .getSubstepValidity([MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT])
                ?.() ?? false;
        }

        // FINAL_CONSENTS hosts statements + fraud (Application Information) and the
        // ACH payment disclosure (Payment). Its own form group is empty — completion
        // is the AND of the three real form groups.
        if (mainStep === MainSteps.SUBMISSION && subStep === PaymentSubSteps.FINAL_CONSENTS) {
            const statementsValid =
                this.formRegistry.getSubstepValidity([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS,
                ])?.() ?? false;
            const achValid =
                this.formRegistry.getSubstepValidity([MainSteps.PAYMENT, PaymentSubSteps.ACH_DISCLAIMER])?.() ?? false;
            return statementsValid && achValid;
        }

        const validitySignal = this.formRegistry.getSubstepValidity([mainStep, subStep]);
        return validitySignal?.() ?? false;
    }

    private orderedSteps = computed(() => {
        const steps = this.baseSteps();
        const order = this.stepsOrder();

        const stepsMap = new Map(steps.map((step) => [step.step, step]));

        return order.mainStepsOrder
            .map((stepKey) => {
                const step = stepsMap.get(stepKey);
                if (!step) return null;

                const subStepsOrder = order.subStepsOrder[stepKey];
                const subStepsMap = new Map(step.subSteps.map((subStep) => [subStep.step, subStep]));

                const orderedSubSteps = subStepsOrder
                    .map((subStepKey) => subStepsMap.get(subStepKey))
                    .filter((subStep) => subStep !== undefined);

                return {
                    ...step,
                    subSteps: orderedSubSteps,
                } as AppStep<MainSteps>;
            })
            .filter((step) => step !== null);
    });

    setStepsOrder(order: StepsOrderConfig) {
        this.stepsOrder.set(order);
    }

    steps = computed(() => {
        const orderedSteps = this.orderedSteps();
        const fullUrl = this.currentUrl();
        const urlWithoutQuery = fullUrl?.split('?')[0].split('#')[0] || '';
        const url = this.getUrlWithoutToken(urlWithoutQuery);

        // First pass: calculate substep completion for all steps
        const stepsWithSubstepState = orderedSteps.map((step) => {
            const isStepActive = url.startsWith(`/${step.path}`);

            const filteredSubSteps = step.subSteps.filter((subStep) => {
                const key = `${step.step}:${subStep.step}`;
                const visibilitySignal = this.substepVisibilitySignals.get(key);
                if (visibilitySignal) {
                    return visibilitySignal();
                }
                return true;
            });

            const subStepsWithState = filteredSubSteps.map((subStep) => {
                let isSubStepActive = false;

                if (subStep.path === '') {
                    isSubStepActive = url === `/${step.path}` || url === `/${step.path}/`;
                } else {
                    const subStepUrl = `/${step.path}/${subStep.path}`;
                    isSubStepActive = url === subStepUrl || url.startsWith(`${subStepUrl}/`) || url === `${subStepUrl}/`;
                }

                const isCompleted = subStep.requiresForm === false ? true : this.getSubstepCompletion(step.step, subStep.step);

                return {
                    ...subStep,
                    active: signal(isSubStepActive),
                    completed: signal(isCompleted),
                };
            });

            const allSubStepsCompleted = subStepsWithState.every((subStep) => subStep.completed());

            return {
                ...step,
                active: signal(isStepActive),
                allSubStepsCompleted,
                subSteps: subStepsWithState,
            };
        });

        // Second pass: Summary completion logic
        // Summary is complete if:
        // 1. Application status is "completed"
        // 2. OR user is on Payment step
        // 3. OR app_payment exists (payment has started)
        const order = this.stepsOrder();
        const summaryIndex = order.mainStepsOrder.indexOf(MainSteps.SUMMARY);
        const paymentIndex = order.mainStepsOrder.indexOf(MainSteps.PAYMENT);
        const summaryBeforePayment = summaryIndex > -1 && paymentIndex > -1 && summaryIndex < paymentIndex;

        return stepsWithSubstepState.map((step) => {
            let isCompleted = step.allSubStepsCompleted;

            if (step.step === MainSteps.SUMMARY && summaryBeforePayment) {
                const isAppCompleted = this.applicationStateService.isCompleted();
                const isOnPaymentStep = url.startsWith('/payment');
                const hasPaymentStarted = this.applicationStateService.hasPaymentStarted();

                isCompleted = isAppCompleted || isOnPaymentStep || hasPaymentStarted;
            }

            return {
                ...step,
                completed: signal(isCompleted),
            };
        });
    });

    currentStepIndex = computed(() => {
        const steps = this.steps();
        return steps.findIndex((step) => step.active());
    });

    currentSubStepIndex = computed(() => {
        const steps = this.steps();
        const currentStep = steps.find((step) => step.active());
        if (!currentStep) return -1;
        return currentStep.subSteps.findIndex((subStep) => subStep.active && subStep.active());
    });

    isFirstStep = computed(() => {
        return this.currentStepIndex() === 0 && this.currentSubStepIndex() <= 0;
    });

    isLastStep = computed(() => {
        const steps = this.steps();
        const stepIndex = this.currentStepIndex();
        if (stepIndex === -1) return false;

        const isLastMainStep = stepIndex === steps.length - 1;
        if (!isLastMainStep) return false;

        const currentStep = steps[stepIndex];
        const subStepIndex = this.currentSubStepIndex();
        return subStepIndex === currentStep.subSteps.length - 1;
    });

    currentSubstepComplete = computed(() => {
        const steps = this.steps();
        const stepIndex = this.currentStepIndex();
        const subStepIndex = this.currentSubStepIndex();

        if (stepIndex === -1 || subStepIndex === -1) return false;

        const currentStep = steps[stepIndex];
        const currentSubStep = currentStep.subSteps[subStepIndex];
        return currentSubStep?.completed?.() ?? false;
    });

    canNavigateToSubstep(targetStepIndex: number, targetSubStepIndex: number): boolean {
        if (!this.substepValidationToggle.isValidationEnabled()) {
            return true;
        }

        const steps = this.steps();

        if (targetStepIndex < 0 || targetStepIndex >= steps.length) {
            return false;
        }

        const targetStep = steps[targetStepIndex];
        if (targetSubStepIndex < 0 || targetSubStepIndex >= targetStep.subSteps.length) {
            return false;
        }

        const currentStepIndex = this.currentStepIndex();
        const currentSubStepIndex = this.currentSubStepIndex();

        if (targetStepIndex < currentStepIndex || (targetStepIndex === currentStepIndex && targetSubStepIndex <= currentSubStepIndex)) {
            return true;
        }

        const targetSubStep = targetStep.subSteps[targetSubStepIndex];
        return targetSubStep?.completed?.() ?? false;
    }

    isStepDisabledByDecline(mainStep: MainSteps): boolean {
        if (!this.applicationProductsService.isDeclined()) return false;
        return mainStep !== MainSteps.SUMMARY;
    }

    isStepDisabledByMissingEmcRisk(mainStep: MainSteps): boolean {
        if (!this.applicationProductsService.isMissingEmcRisk()) return false;
        return mainStep !== MainSteps.PERSONAL_DETAILS && mainStep !== MainSteps.PRODUCT_SELECTION;
    }

    navigateToNext() {
        this.clearCurrentSubstepBadge();

        if (this.globalStateService.isSaving()) {
            return;
        }

        const steps = this.steps();
        const stepIndex = this.currentStepIndex();
        const subStepIndex = this.currentSubStepIndex();
        const token = this.getToken();

        if (stepIndex === -1 || !token) return;

        const currentStep = steps[stepIndex];

        if (subStepIndex < currentStep.subSteps.length - 1) {
            const nextSubStep = currentStep.subSteps[subStepIndex + 1];
            const path = nextSubStep.path === '' ? `/${currentStep.path}/${token}` : `/${currentStep.path}/${nextSubStep.path}/${token}`;
            this.router.navigate([path]);
            return;
        }

        // Skip main steps that have no visible substeps
        for (let nextIdx = stepIndex + 1; nextIdx < steps.length; nextIdx++) {
            const nextStep = steps[nextIdx];
            const firstSubStep = nextStep.subSteps[0];
            if (!firstSubStep) continue;
            const path = firstSubStep.path === '' ? `/${nextStep.path}/${token}` : `/${nextStep.path}/${firstSubStep.path}/${token}`;
            this.router.navigate([path]);
            return;
        }
    }

    navigateToPrevious() {
        this.clearCurrentSubstepBadge();

        if (this.globalStateService.isSaving()) {
            return;
        }

        const steps = this.steps();
        const stepIndex = this.currentStepIndex();
        const subStepIndex = this.currentSubStepIndex();
        const token = this.getToken();

        if (stepIndex === -1 || !token) return;

        const currentStep = steps[stepIndex];
        let path: string | null = null;

        if (subStepIndex === -1) {
            // Current URL points at a substep hidden from the stepper (e.g. /submission/submit).
            // Back should land on the last visible substep of the current main step rather
            // than walking back across main steps.
            const lastVisibleSub = currentStep.subSteps[currentStep.subSteps.length - 1];
            if (lastVisibleSub) {
                path = lastVisibleSub.path === '' ? `/${currentStep.path}/${token}` : `/${currentStep.path}/${lastVisibleSub.path}/${token}`;
            }
        } else if (subStepIndex > 0) {
            const prevSubStep = currentStep.subSteps[subStepIndex - 1];
            path = prevSubStep.path === '' ? `/${currentStep.path}/${token}` : `/${currentStep.path}/${prevSubStep.path}/${token}`;
        } else {
            // Walk back across main steps, skipping any with no visible substeps
            for (let prevIdx = stepIndex - 1; prevIdx >= 0; prevIdx--) {
                const prevStep = steps[prevIdx];
                const lastSubStep = prevStep.subSteps[prevStep.subSteps.length - 1];
                if (!lastSubStep) continue;
                path = lastSubStep.path === '' ? `/${prevStep.path}/${token}` : `/${prevStep.path}/${lastSubStep.path}/${token}`;
                break;
            }
        }

        if (path) {
            if (this.getUnsavedChangesService().requestNavigation(path)) return;
            this.router.navigateByUrl(path);
        }
    }

    private clearCurrentSubstepBadge(): void {
        const steps = this.steps();
        const stepIndex = this.currentStepIndex();
        const subStepIndex = this.currentSubStepIndex();

        if (stepIndex === -1 || subStepIndex === -1) return;

        const currentStep = steps[stepIndex];
        const currentSubStep = currentStep.subSteps[subStepIndex];
        const key = `${currentStep.step}:${currentSubStep.step}`;

        const currentMap = this.newlyAddedSubsteps();
        if (!currentMap.has(key)) return;

        const updatedMap = new Map(currentMap);
        updatedMap.delete(key);
        this.newlyAddedSubsteps.set(updatedMap);

        this.badgeRemovalSubscriptions.get(key)?.unsubscribe();
        this.badgeRemovalSubscriptions.delete(key);
    }

    private controlValue$<T = any>(path: string[]): Observable<T> {
        const control = this.getControlByPath(path);
        if (!control) return of(null as T);
        return control.valueChanges.pipe(startWith(control.value));
    }

    private getControlByPath(path: string[]): AbstractControl | null {
        let control: AbstractControl | null = this.applicationFormService.applicationForm;
        for (const segment of path) {
            control = control?.get(segment) ?? null;
            if (!control) break;
        }
        return control;
    }

    private handleSubstepAdded(mainStep: MainSteps, subStep: AllSubSteps): void {
        const key = `${mainStep}:${subStep}`;
        const currentMap = this.newlyAddedSubsteps();
        const updatedMap = new Map(currentMap);
        updatedMap.set(key, Date.now());
        this.newlyAddedSubsteps.set(updatedMap);

        // Set up form value change listener to remove badge when user interacts
        this.setupBadgeRemovalOnInteraction(mainStep, subStep);
    }

    private handleSubstepRemoved(mainStep: MainSteps, subStep: AllSubSteps): void {
        const key = `${mainStep}:${subStep}`;

        this.badgeRemovalSubscriptions.get(key)?.unsubscribe();
        this.badgeRemovalSubscriptions.delete(key);

        this.formRegistry.getFormGroup(mainStep, subStep)?.reset();

        const currentMap = this.newlyAddedSubsteps();
        const updatedMap = new Map(currentMap);
        updatedMap.delete(key);
        this.newlyAddedSubsteps.set(updatedMap);
    }

    isSubstepNew(mainStep: MainSteps, subStep: AllSubSteps): boolean {
        const key = `${mainStep}:${subStep}`;
        return this.newlyAddedSubsteps().has(key);
    }

    private setupBadgeRemovalOnInteraction(mainStep: MainSteps, subStep: AllSubSteps): void {
        const formGroup = this.formRegistry.getFormGroup(mainStep, subStep);
        if (!formGroup) return;

        const key = `${mainStep}:${subStep}`;

        this.badgeRemovalSubscriptions.get(key)?.unsubscribe();

        const subscription = formGroup.valueChanges.subscribe(() => {
            const currentMap = this.newlyAddedSubsteps();
            const formHasUserInputedValues = this.formRegistry.formHasUserInputedValues(formGroup, mainStep, subStep);

            if (currentMap.has(key) && formHasUserInputedValues) {
                const updatedMap = new Map(currentMap);
                updatedMap.delete(key);
                this.newlyAddedSubsteps.set(updatedMap);
                subscription.unsubscribe();
                this.badgeRemovalSubscriptions.delete(key);
            }
        });

        this.badgeRemovalSubscriptions.set(key, subscription);
    }

}
